import { PageCanvas } from "@/components/page-canvas";
import { useRef, useState } from "react";
import { Form, Head, Link, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import AppLayout from "@/layouts/app_layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { FormErrors, errorText } from "@/components/forms/form-errors";
import {
  locationRequestsPath,
  locationRequestPath,
  editLocationRequestPath,
  locationRequestResponsePath,
  locationRequestRoutingPath,
  newLocationJobPath,
  locationJobPath,
  newLocationRequestProposalDraftPath,
  locationRequestProposalDraftPath,
} from "@/routes";
import { Choice } from "./controls";
import type { Enquiry, Pagination } from "./types";
import { formatLocationTime } from "@/lib/location-time";
type History = {
  id: string;
  action: string;
  state: string;
  note: string;
  suggested_time: string;
  occurred_at: string;
  actor: { id: string; name: string } | null;
};
export default function RequestShow({
  enquiry,
  history,
  pagination,
  routing_locations,
  channel_messages,
  message_pagination,
  messenger,
  section = "conversation",
  routing = false,
}: {
  enquiry: Enquiry;
  section: "conversation" | "details" | "activity";
  routing: boolean;
  message_pagination: Pagination;
  channel_messages: {
    id: string;
    body: string;
    attachment_types: string[];
    sent_at: string;
  }[];
  messenger: {
    window_ends_at: string | null;
    sending_available: boolean;
  } | null;
  history: History[];
  pagination: Pagination;
  routing_locations: { id: string; name: string }[];
}) {
  const { auth, i18n, errors } = usePage().props;
  const { t } = useTranslation();
  const [action, setAction] = useState("responded"),
    [routingOpen, setRoutingOpen] = useState(routing);
  const trigger = useRef<HTMLButtonElement>(null);
  if (!auth.location) return null;
  const location = auth.location.id;
  const title =
    enquiry.customer?.name ||
    (enquiry.channel === "whatsapp"
      ? t("whatsapp.enquiry")
      : enquiry.channel === "instagram"
        ? t("instagram.enquiry")
        : enquiry.channel_identity) ||
    t("requests.unnamed");
  const next = enquiry.job_id
    ? {
        href: locationJobPath(location, enquiry.job_id),
        label: t("requests.view_job"),
      }
    : enquiry.proposal_draft
      ? {
          href: locationRequestProposalDraftPath(location, enquiry.id),
          label: t("proposals.preview"),
        }
      : enquiry.uncommitted && !enquiry.ready
        ? {
            href: editLocationRequestPath(location, enquiry.id),
            label: t("requests.complete_details"),
          }
        : enquiry.uncommitted && enquiry.ready
          ? {
              href: newLocationRequestProposalDraftPath(location, enquiry.id),
              label: t("proposals.compose"),
            }
          : null;
  const social = ["facebook", "instagram", "whatsapp"].includes(
    enquiry.channel,
  );
  const time = (value: string) =>
    formatLocationTime(value, i18n, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  const pages = (value: Pagination, messages = false) =>
    value.pages > 1 && (
      <nav
        className="flex flex-wrap items-center gap-4"
        aria-label={t("requests.pages")}
      >
        {value.page > 1 && (
          <Link
            className="underline"
            href={locationRequestPath(location, enquiry.id, {
              section,
              [messages ? "messages_page" : "page"]: value.page - 1,
            })}
          >
            {t("requests.previous")}
          </Link>
        )}
        <span>{t("requests.page", value)}</span>
        {value.page < value.pages && (
          <Link
            className="underline"
            href={locationRequestPath(location, enquiry.id, {
              section,
              [messages ? "messages_page" : "page"]: value.page + 1,
            })}
          >
            {t("requests.next")}
          </Link>
        )}
      </nav>
    );
  return (
    <AppLayout
      breadcrumbs={[
        { title: t("requests.title"), href: locationRequestsPath(location) },
      ]}
    >
      <Head title={title} />
      <PageCanvas>
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 space-y-2">
            <h1 className="text-page font-display break-words">{title}</h1>
            <p className="text-sm text-muted-foreground">
              {auth.location.name} · {t(`requests.channels.${enquiry.channel}`)}
            </p>
            <div className="flex flex-wrap gap-2">
              <Badge>{t(`requests.states.${enquiry.state}`)}</Badge>
              <Badge variant="outline">
                {t(
                  enquiry.complete
                    ? "requests.complete"
                    : "requests.incomplete",
                )}
              </Badge>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            {next && (
              <Button render={<Link href={next.href} />}>{next.label}</Button>
            )}
            {routing_locations.length > 0 && (
              <Button
                type="button"
                variant="outline"
                ref={trigger}
                onClick={() => setRoutingOpen(true)}
              >
                {t("requests.route")}
              </Button>
            )}
          </div>
        </header>
        {!routingOpen && (
          <FormErrors
            errors={errors || {}}
            targets={{ note: "request-note", suggested_time: "request-time" }}
          />
        )}
        <nav
          aria-label={t("workflow.request_navigation")}
          className="flex flex-wrap gap-2 border-b pb-3"
        >
          {(["conversation", "details", "activity"] as const).map((value) => (
            <Button
              key={value}
              variant={section === value ? "secondary" : "ghost"}
              render={
                <Link
                  href={locationRequestPath(location, enquiry.id, {
                    section: value,
                  })}
                  aria-current={section === value ? "page" : undefined}
                />
              }
            >
              {t(`workflow.${value}`)}
            </Button>
          ))}
        </nav>
        {section === "conversation" && (
          <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_22rem]">
            <section
              className="min-w-0 space-y-5"
              aria-label={t("workflow.conversation")}
            >
              <h2 className="text-lg font-semibold">
                {t("workflow.conversation")}
              </h2>
              {social ? (
                <>
                  {!channel_messages.length && (
                    <p className="text-muted-foreground">
                      {t("workflow.no_messages")}
                    </p>
                  )}
                  {channel_messages.map((message) => (
                    <article
                      className="space-y-2 rounded-lg border p-4"
                      key={message.id}
                    >
                      <p className="text-sm text-muted-foreground">
                        {time(message.sent_at)}
                      </p>
                      {message.body && (
                        <p className="whitespace-pre-wrap break-words">
                          {message.body}
                        </p>
                      )}
                      {message.attachment_types.length > 0 && (
                        <p className="text-sm text-muted-foreground">
                          {t(`${enquiry.channel}.attachments`, {
                            count: message.attachment_types.length,
                          })}
                        </p>
                      )}
                    </article>
                  ))}
                  {pages(message_pagination, true)}
                  {messenger?.window_ends_at && (
                    <p className="text-sm text-muted-foreground">
                      {t(`${enquiry.channel}.window`, {
                        time: time(messenger.window_ends_at),
                      })}
                    </p>
                  )}
                  <p className="text-sm text-muted-foreground">
                    {t(`${enquiry.channel}.outbound_unavailable`)}
                  </p>
                </>
              ) : (
                <article className="rounded-lg border p-4 whitespace-pre-wrap break-words">
                  {enquiry.body}
                </article>
              )}
              {enquiry.uncommitted && (
                <section className="space-y-4 rounded-lg border p-4">
                  <div>
                    <h2 className="font-semibold">
                      {t("requests.record_response")}
                    </h2>
                    <p className="mt-2 text-sm text-muted-foreground">
                      {t("requests.response_help")}
                    </p>
                  </div>
                  <Form
                    action={locationRequestResponsePath(location, enquiry.id)}
                    method="post"
                    resetOnSuccess={[
                      "response[note]",
                      "response[suggested_time]",
                    ]}
                  >
                    {({ processing, errors: formErrors }) => (
                      <div className="space-y-4">
                        <input
                          type="hidden"
                          name="revision"
                          value={enquiry.revision}
                        />
                        <Choice
                          id="request-action"
                          name="response[action]"
                          label={t("requests.action")}
                          defaultValue="responded"
                          onValueChange={(value) =>
                            setAction(value || "responded")
                          }
                          options={["responded", "declined", "rescheduled"].map(
                            (id) => ({ id, name: t(`requests.actions.${id}`) }),
                          )}
                        />
                        <div className="space-y-2">
                          <Label htmlFor="request-note">
                            {t("requests.note")}
                          </Label>
                          <Textarea
                            id="request-note"
                            name="response[note]"
                            required
                            maxLength={10000}
                            aria-invalid={!!formErrors.note}
                          />
                          {formErrors.note && (
                            <p className="text-sm text-destructive">
                              {errorText(formErrors.note)}
                            </p>
                          )}
                        </div>
                        {action === "rescheduled" && (
                          <div className="space-y-2">
                            <Label htmlFor="request-time">
                              {t("requests.suggested_time")}
                            </Label>
                            <Textarea
                              id="request-time"
                              name="response[suggested_time]"
                              required
                              maxLength={10000}
                              aria-invalid={!!formErrors.suggested_time}
                            />
                            {formErrors.suggested_time && (
                              <p className="text-sm text-destructive">
                                {errorText(formErrors.suggested_time)}
                              </p>
                            )}
                            <p className="text-sm text-muted-foreground">
                              {t("requests.suggestion_help")}
                            </p>
                          </div>
                        )}
                        <Button
                          type="submit"
                          variant="outline"
                          disabled={processing}
                        >
                          {t("requests.record")}
                        </Button>
                      </div>
                    )}
                  </Form>
                </section>
              )}
            </section>
            <aside className="space-y-4 rounded-lg border p-4">
              <h2 className="font-semibold">{t("requests.details")}</h2>
              <p className="whitespace-pre-wrap break-words">
                {enquiry.service_details || t("requests.services_missing")}
              </p>
              {enquiry.vehicle && (
                <p>
                  {enquiry.vehicle.make} {enquiry.vehicle.model} ·{" "}
                  {enquiry.vehicle.registration_number}
                </p>
              )}
              {enquiry.uncommitted && enquiry.ready && (
                <Button
                  variant="outline"
                  render={
                    <Link
                      href={newLocationJobPath(location, {
                        request_id: enquiry.id,
                      })}
                    />
                  }
                >
                  {t("jobs.new")}
                </Button>
              )}
              {enquiry.uncommitted && enquiry.ready && (
                <Link
                  className="block py-2 text-sm underline"
                  href={editLocationRequestPath(location, enquiry.id)}
                >
                  {t("requests.complete_details")}
                </Link>
              )}
            </aside>
          </div>
        )}
        {section === "details" && (
          <section className="max-w-4xl space-y-5">
            <h2 className="text-lg font-semibold">{t("requests.details")}</h2>
            <dl className="grid gap-4 rounded-lg border p-4 sm:grid-cols-2">
              <div>
                <dt className="text-sm text-muted-foreground">
                  {t("requests.intake_location")}
                </dt>
                <dd>{enquiry.intake_location_name}</dd>
              </div>
              {!["instagram", "whatsapp"].includes(enquiry.channel) &&
                enquiry.channel_identity && (
                  <div>
                    <dt className="text-sm text-muted-foreground">
                      {t("requests.channel_identity")}
                    </dt>
                    <dd className="break-words">{enquiry.channel_identity}</dd>
                  </div>
                )}
              {enquiry.customer && (
                <div>
                  <dt className="text-sm text-muted-foreground">
                    {t("workflow.customer")}
                  </dt>
                  <dd className="break-words">
                    {enquiry.customer.name} ·{" "}
                    {enquiry.customer.email || enquiry.customer.phone}
                  </dd>
                </div>
              )}
              {enquiry.vehicle && (
                <div>
                  <dt className="text-sm text-muted-foreground">
                    {t("workflow.vehicle")}
                  </dt>
                  <dd>
                    {enquiry.vehicle.make} {enquiry.vehicle.model} ·{" "}
                    {enquiry.vehicle.registration_number}
                  </dd>
                </div>
              )}
            </dl>
            <p className="whitespace-pre-wrap break-words">
              {enquiry.service_details || t("requests.services_missing")}
            </p>
            {enquiry.channel === "website" && (
              <section className="space-y-3 rounded-lg border p-4">
                <h3 className="font-semibold">
                  {t("website_requests.snapshot")}
                </h3>
                {Object.entries(enquiry.intake_details)
                  .filter(([, value]) => value && typeof value === "string")
                  .map(([key, value]) => (
                    <p key={key} className="break-words">
                      {String(value)}
                    </p>
                  ))}
                {enquiry.intake_details.identity_pending && (
                  <p>{t("website_requests.pending")}</p>
                )}
                {enquiry.desired_start_at && enquiry.desired_end_at && (
                  <p>
                    {[enquiry.desired_start_at, enquiry.desired_end_at]
                      .map((value) =>
                        formatLocationTime(
                          value,
                          {
                            locale: i18n.locale,
                            time_zone: enquiry.desired_time_zone,
                          },
                          { dateStyle: "medium", timeStyle: "short" },
                        ),
                      )
                      .join(" – ")}
                  </p>
                )}
              </section>
            )}
            {enquiry.attachments.map((file) => (
              <a
                className="block py-2 break-words underline"
                href={file.path}
                key={file.path}
              >
                {file.name}
              </a>
            ))}
            {enquiry.uncommitted && (
              <Button
                variant="outline"
                render={
                  <Link href={editLocationRequestPath(location, enquiry.id)} />
                }
              >
                {t("requests.complete_details")}
              </Button>
            )}
          </section>
        )}
        {section === "activity" && (
          <section className="max-w-4xl space-y-5">
            <h2 className="text-lg font-semibold">{t("requests.history")}</h2>
            <ol className="space-y-5">
              {history.map((event) => (
                <li key={event.id} className="space-y-2 border-l-2 pl-4">
                  <p className="font-medium">
                    {t(`requests.history_actions.${event.action}`, {
                      defaultValue: t("requests.updated"),
                    })}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {event.actor?.name || t("requests.system")} ·{" "}
                    {time(event.occurred_at)}
                  </p>
                  {event.note && (
                    <p className="whitespace-pre-wrap break-words">
                      {event.note}
                    </p>
                  )}
                  {event.suggested_time && (
                    <p className="whitespace-pre-wrap break-words">
                      {event.suggested_time}
                    </p>
                  )}
                </li>
              ))}
            </ol>
            {pages(pagination)}
          </section>
        )}
        <Sheet open={routingOpen} onOpenChange={setRoutingOpen}>
          <SheetContent
            className="w-full! overflow-y-auto sm:max-w-lg!"
            finalFocus={trigger}
          >
            <SheetHeader>
              <SheetTitle>{t("requests.route")}</SheetTitle>
              <SheetDescription>{t("requests.routing_help")}</SheetDescription>
            </SheetHeader>
            <Form
              action={locationRequestRoutingPath(location, enquiry.id)}
              method="post"
              onSuccess={() => setRoutingOpen(false)}
              className="px-4 pb-4"
            >
              {({ processing, errors: formErrors }) => (
                <div className="space-y-5">
                  <FormErrors
                    errors={formErrors}
                    targets={{ target_location: "request-location" }}
                  />
                  <input type="hidden" name="return_section" value={section} />
                  <input
                    type="hidden"
                    name="revision"
                    value={enquiry.revision}
                  />
                  <Choice
                    id="request-location"
                    name="target_location_id"
                    label={t("common.location")}
                    options={routing_locations}
                  />
                  <div className="flex flex-wrap gap-3">
                    <Button type="submit" disabled={processing}>
                      {t("requests.route")}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setRoutingOpen(false)}
                    >
                      {t("common.cancel")}
                    </Button>
                  </div>
                </div>
              )}
            </Form>
          </SheetContent>
        </Sheet>
      </PageCanvas>
    </AppLayout>
  );
}
